/**
 * Environments (docs/environments.md): the pure helpers (URL, colour, audit phrases) and the one place that
 * remembers which environment the app is looking at. The choice is explicit only once someone made it (or a
 * link named it): until then no `env` is sent and the cloud uses the tenant's default.
 */

export const ENV_PARAM = 'env';
const STORAGE_KEY = 'mv.env';

export interface Environment {
  id: string; name: string; color: string; position: number; protected: boolean; isDefault: boolean;
  vaultCount: number; onlineCount: number;
}

// ---------------------------------------------------------------- URLs

/**
 * `to` (an in-app path with an optional query and hash) with `env=<envId>` in its query. An `env` the path
 * already has is kept (a link may name another environment on purpose); no envId or an external URL: unchanged.
 */
export function withEnv(to: string, envId: string | null | undefined): string {
  if (!envId || !to.startsWith('/') || to.startsWith('//')) return to;
  const hash = to.indexOf('#');
  const head = hash < 0 ? to : to.slice(0, hash);
  const tail = hash < 0 ? '' : to.slice(hash);
  const q = head.indexOf('?');
  const path = q < 0 ? head : head.slice(0, q);
  const params = new URLSearchParams(q < 0 ? '' : head.slice(q + 1));
  if (!params.has(ENV_PARAM)) params.set(ENV_PARAM, envId);
  return `${path}?${params}${tail}`;
}

/** The search string (with or without "?") with its `env` set, or removed when envId is null; the rest as it was. */
export function searchWithEnv(search: string, envId: string | null): string {
  const params = new URLSearchParams(search);
  if (envId) params.set(ENV_PARAM, envId);
  else params.delete(ENV_PARAM);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export const envOfSearch = (search: string): string | null => new URLSearchParams(search).get(ENV_PARAM) || null;

/** Where switching to `envId` goes from the current location: the same page, or Now when it is one document. */
export function switchTarget(pathname: string, search: string, envId: string): string {
  if (pathname.startsWith('/d/')) return searchWithEnv('', envId) ? `/${searchWithEnv('', envId)}` : '/';
  return `${pathname}${searchWithEnv(search, envId)}`;
}

/** The environment to use: the requested one when the tenant has it, else null (= the default). One environment: always null. */
export function resolveEnv(requested: string | null | undefined, envs: { id: string }[]): string | null {
  if (envs.length < 2 || !requested) return null;
  return envs.some((e) => e.id === requested) ? requested : null;
}

/** The switcher is noise for a tenant with the one, plain "Default" environment. */
export function showSwitcher(envs: Pick<Environment, 'name' | 'protected' | 'isDefault'>[]): boolean {
  if (envs.length >= 2) return true;
  const only = envs[0];
  return !!only && !(only.isDefault && only.name === 'Default' && !only.protected);
}

/** Whether the band marks this environment: every one but the default, and a protected one always. */
export const showBand = (e: Pick<Environment, 'isDefault' | 'protected'>): boolean => !e.isDefault || e.protected;

// ---------------------------------------------------------------- colour

export const ENV_COLORS = ['gray', 'blue', 'green', 'teal', 'amber', 'orange', 'red', 'pink', 'purple', 'indigo'] as const;
const HEX = /^#[0-9a-f]{6}$/i;

/** A CSS colour for an environment colour: the named ones are the --env-* tokens (design/tokens.css), a hex stays as it is. */
export function envColor(color: string | null | undefined): string {
  if (color && HEX.test(color)) return color;
  return `var(--env-${(ENV_COLORS as readonly string[]).includes(color ?? '') ? color : 'gray'})`;
}

// ---------------------------------------------------------------- the current environment

let current: string | null = null;
const listeners = new Set<() => void>();

function initial(): string | null {
  if (typeof window === 'undefined') return null;
  const fromUrl = envOfSearch(window.location.search);
  if (fromUrl) return fromUrl;
  try {
    return window.localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}
current = initial();

/** The environment id sent with tenant requests; null = none named, the cloud uses the default. */
export const getEnvId = (): string | null => current;

export function setEnvId(id: string | null): void {
  if (id === current) return;
  current = id;
  if (typeof window !== 'undefined') {
    try {
      if (id) window.localStorage.setItem(STORAGE_KEY, id);
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // storage unavailable: the choice lasts for this page only
    }
  }
  for (const l of listeners) l();
}

export function subscribeEnv(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Tenant routes that are about the tenant, not an environment: they never get one. A single vault's own page (its detail, notes, rename) belongs here: a vault is found whichever environment it is in. */
const TENANT_LEVEL = /^\/api\/tenants\/[^/]+\/(environments|audit|users|install-tokens|vaults\/[^/?]+(\/notes(\/\d+)?|\/config|\/restart|\/logs)?(\?|$))/;

/** Whether a request to this API path carries the current environment. */
export const takesEnv = (path: string): boolean => path.startsWith('/api/tenants/') && !TENANT_LEVEL.test(path);

// ---------------------------------------------------------------- audit log

export interface AuditEntry { id: number; at: number; user: string | null; action: string; target: string | null; details: any }

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const q = (v: string | null) => (v ? `"${v}"` : null);

const CATALOG_FIELD: Record<string, string> = { displayName: 'name', displayPlural: 'plural', description: 'description', domain: 'domain', tags: 'tags', steward: 'steward', sensitivity: 'sensitivity', system: 'system' };
const SYSTEM_STATUS_WORD: Record<string, string> = { checked: 'as checked', hidden: 'as hidden', new: 'as new again' };
const CATALOG_STATUS_WORD: Record<string, string> = { checked: 'as checked', deprecated: 'as deprecated', new: 'as new again' };

/** One audit entry in plain language: who did it is `who`, `text` the phrase, `target` the thing it was done to. */
export function auditPhrase(e: AuditEntry): { who: string; text: string; target: string | null } {
  const d = e.details ?? {};
  const name = str(d.name);
  const envName = str(d.environmentName);
  const who = e.user === 'install-token' ? 'An install token' : e.user ?? 'The system';
  const changes = (d.changes ?? {}) as Record<string, any>;
  switch (e.action) {
    case 'environment.create': return { who, text: 'created the environment', target: name };
    case 'environment.delete': return { who, text: 'deleted the environment', target: name };
    case 'environment.set_default': return { who, text: 'made the default environment', target: name };
    case 'environment.update': {
      const parts: string[] = [];
      if (changes.name) parts.push(`renamed from ${changes.name.from}`);
      if ('protected' in changes) parts.push(changes.protected ? 'protected' : 'no longer protected');
      if (changes.color) parts.push('colour changed');
      if (changes.position) parts.push('reordered');
      return { who, text: parts.length ? `updated the environment (${parts.join(', ')})` : 'updated the environment', target: name };
    }
    case 'vault.register': return { who, text: `registered a vault in ${q(envName) ?? 'an environment'}`, target: name };
    case 'vault.revoke': return { who, text: 'revoked the vault', target: name };
    case 'vault.move': return { who, text: `moved the vault from ${q(str(d.fromName)) ?? 'an environment'} to ${q(str(d.toName)) ?? 'another'}`, target: name };
    case 'vault.update': return { who, text: d.ingestUrlOverride ? 'set the address of the vault' : 'cleared the address of the vault', target: name };
    case 'install_token.create': return { who, text: `created an install token for ${q(envName) ?? 'an environment'}`, target: null };
    case 'connector_key.create': return { who, text: `created a connector key for ${q(envName) ?? 'an environment'}`, target: name };
    case 'connector_key.revoke': return { who, text: 'revoked the connector key', target: name };
    case 'catalog.update': return { who, text: `edited the details of the data model${Object.keys(changes).length ? ` (${Object.keys(changes).map((k) => CATALOG_FIELD[k] ?? k).join(', ')})` : ''}`, target: e.target };
    case 'catalog.status': {
      const n = Number(d.count) || 1;
      const what = CATALOG_STATUS_WORD[str(d.status) ?? ''] ?? `as ${str(d.status)}`;
      return { who, text: n === 1 ? `marked the data model ${what}` : `marked ${n} data models ${what}`, target: n === 1 ? e.target : null };
    }
    case 'system.create': return { who, text: 'added the system', target: name ?? e.target };
    case 'system.update': {
      const merged = Array.isArray(d.merged) && d.merged.length ? `, merged ${d.merged.join(', ')} into it` : '';
      return { who, text: `edited the system${Object.keys(changes).length ? ` (${Object.keys(changes).join(', ')})` : ''}${merged}`, target: name ?? e.target };
    }
    case 'system.logo': return { who, text: d.removed ? 'removed the logo of the system' : 'set the logo of the system', target: name ?? e.target };
    case 'system.status': {
      const n = Number(d.count) || 1;
      const what = SYSTEM_STATUS_WORD[str(d.status) ?? ''] ?? `as ${str(d.status)}`;
      return { who, text: n === 1 ? `marked the system ${what}` : `marked ${n} systems ${what}`, target: n === 1 ? e.target : null };
    }
    default: return { who, text: e.action.replace(/[._]/g, ' '), target: name ?? e.target };
  }
}
